import type {Location} from "history";
import orderBy from "lodash/orderBy";
import {LoadedCardOrder} from "../card-order/useCardOrder";
import RAW_ORDERS from "../card-order/order-info";
import {MutableRefObject, ReactNode} from "react";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import {api} from "../../lib/api";
import {Col, css} from "@cdx/ds";
import {transitions} from "@cdx/ds/css/decoration.css";
import {StickyTopAwareStickyBox} from "../card-container/card-container-utils";
import {Card} from "../../cdx-models/Card";
import {Notifications} from "../notifications/useNotifications";

const ORDERS = RAW_ORDERS as any as AllOrders;
export type CardUrlOpts = {
  card: Card;
  targetPanel?: string;
  resolvableId?: string;
  cardContainerKey?: string;
};

export type ArenaCtx = {
  type: string;
  shortcutCategory: string;
  model: any | null;
  routes: {
    getActiveCard: (location: Location) => any | null;
    getCardUrl: (opts: CardUrlOpts) => string;
    goToCard: (opts: {
      cardId: string;
      targetPanel?: string;
      resolvableId?: string;
      cardContainerKey?: string;
    }) => void;
    rootPath: string;
    getRootUrl: (opts?: any) => string;
    stateBased: boolean;
  };
};

export type CardPanelBag = {
  cards: any[];
  isDraggingCardIds: Set<string>;
  projects: any[];
  root: any;
  notifications: Notifications;
  location: Location;
  arenaCtx: ArenaCtx;
  activeCard: any | null;
  onLight: boolean;
  canCreateCard: boolean | null;
  scrollElementRef: MutableRefObject<HTMLElement | Window>;
  shownManualOrder: string | null;
};

type CardWithKeys = {
  card: any;
  scalarKey: string;
  key: any;
  orderProp: null | string;
  cardOrder: LoadedCardOrder;
  flipKey: string;
};

type SoloKeyOrderInfo<TKey> = {
  extractKey: (card: any, prepared?: any) => TKey;
  multiKey?: boolean;
};

type MultiKeyOrderInfo<TKey> = {
  extractKey: (card: any, prepared?: any) => TKey[];
  multiKey: true;
};

type DroppableOrderInfo<TKey> = {
  cannotDrop: (key: TKey, card: any) => boolean;
  cannotDropLabel: (key: TKey, card: any) => string;
  onDrop: (cards: any[], orderKey: TKey) => any;
  dropLabels: (key: TKey, label: ReactNode) => ReactNode;
};

type OrderInfo<TKey> = (SoloKeyOrderInfo<TKey> | MultiKeyOrderInfo<TKey>) & {
  extractSecondarySortValue: (card: any, preparedData?: any, keyAsValue?: any) => any;
  prepareExtractData: (bag: OrderBag) => any;
  getLabel: (
    key: TKey,
    opts: {
      onLight?: boolean;
      mode: "cardPanel" | "dropZone" | "subCards" | "dropOverlay";
      root: any;
      arenaCtx: ArenaCtx;
    }
  ) => ReactNode;
  secondaryOrderProps?: {prop: string; isReversed: boolean}[];
  toSortKey: (keyAsValue: any, root: any) => string;
  keyToScalar?: (key: TKey) => string;
  allKeys?: () => TKey[];
  getQuickCreateProps?: (key: TKey) => any;
  getLabelSearchFilter?: (key: TKey) => {category: string; value: any};
} & (DroppableOrderInfo<TKey> | undefined);

export type AllOrders = {[orderProp: string]: OrderInfo<any>};

export type SwimlaneGroup = {
  cardsWithKeys: {card: any; flipKey: string; keyAsValue: any}[];
  keyAsValue: any;
  groupOrderInfo: null | OrderInfo<any>;
  orderProp: string | null;
  scalarKey: string;
};

type OrderBag = {root: any; projects: any[]; notifications: Notifications};

const processGroups = (opts: {
  cardAndKeys: CardWithKeys[];
  cardOrder: LoadedCardOrder;
  bag: OrderBag;
}) => {
  const {cardAndKeys, cardOrder, bag} = opts;
  const {root} = bag;
  const groups = new Map<string, SwimlaneGroup>();
  if (cardAndKeys.length === 0) return {groups, orderedGroupKeys: []};

  cardAndKeys.forEach(({card, key, scalarKey, orderProp: innerOrderProp, flipKey}) => {
    const existing = groups.get(scalarKey);
    if (existing) {
      existing.cardsWithKeys.push({card, flipKey, keyAsValue: key});
    } else {
      groups.set(scalarKey, {
        cardsWithKeys: [{card, flipKey, keyAsValue: key}],
        keyAsValue: key,
        // this might differ from bag.orderInfo when transitioning from one order to the next one
        // orderProp is null if `isCompact` is active
        groupOrderInfo: innerOrderProp ? ORDERS[innerOrderProp] : null,
        orderProp: innerOrderProp,
        scalarKey,
      });
    }
  });

  const groupKeys = [...groups.keys()];

  const orderedGroupKeys = orderBy(
    groupKeys,
    (keyAsScalar) => {
      const {keyAsValue, groupOrderInfo} = groups.get(keyAsScalar)!;
      return groupOrderInfo?.toSortKey(keyAsValue, root);
    },
    cardOrder.isReversed ? "desc" : "asc"
  );

  const dropSecondaryProps = new Set();

  const buildSecondarySortList = () => {
    const list: {prop: string; isReversed: boolean}[] = [];
    if (cardOrder.isCompact) {
      // there's only a single swimlane, so sort that swimlane by the primary order first
      list.push({prop: cardOrder.prop, isReversed: cardOrder.isReversed});
    } else {
      if (!cardOrder.secondary?.length) {
        // if the user did not specify a custom secondary order, use the one that the primary order recommends
        const {secondaryOrderProps} = ORDERS[cardOrder.prop];
        if (secondaryOrderProps) list.push(...secondaryOrderProps);
      }
    }
    if (cardOrder.secondary) list.push(...cardOrder.secondary);
    list.push(
      {prop: "priority", isReversed: false},
      {prop: "effort", isReversed: true},
      {prop: "accountSeq", isReversed: false}
    );
    return list;
  };

  const secondarySortFns = buildSecondarySortList()
    .filter(({prop}) => {
      if (dropSecondaryProps.has(prop)) return false;
      dropSecondaryProps.add(prop);
      return true;
    })
    .map(({prop, isReversed: secReversed}) => {
      const {toSortKey, extractKey, extractSecondarySortValue, prepareExtractData} = ORDERS[prop];
      const preparedData = prepareExtractData && prepareExtractData(bag);
      const extractSortVal = (card: any, key: any) =>
        extractSecondarySortValue
          ? extractSecondarySortValue(card, prepareExtractData, key)
          : toSortKey(extractKey(card, preparedData), root);
      return (cardA: any, cardB: any, keyA: any, keyB: any) => {
        const propA = extractSortVal(cardA, keyA);
        const propB = extractSortVal(cardB, keyB);

        if (propA !== propB) {
          if (propB === null) return secReversed ? 1 : -1;
          if (propA === null) return secReversed ? -1 : 1;
          if (propA > propB) return secReversed ? -1 : 1;
          if (propA < propB) return secReversed ? 1 : -1;
        }
        return 0;
      };
    });

  orderedGroupKeys.forEach((keyAsScalar) => {
    const groupInfo = groups.get(keyAsScalar)!;
    groupInfo.cardsWithKeys.sort((a, b) => {
      for (let i = 0; i < secondarySortFns.length; i += 1) {
        const cmp = secondarySortFns[i](a.card, b.card, a.keyAsValue, b.keyAsValue);
        if (cmp !== 0) return cmp;
      }
      return 0;
    });
  });

  return {groups, orderedGroupKeys};
};

const getCardGroups = ({cards, cardOrder, bag}: OrderedCardGroupArg) => {
  if (cards.length === 0) return [];
  if (cardOrder.isCompact) return cards.map((card) => ({card, scalarKeys: [null], keys: [null]}));
  const primaryOrderInfo = ORDERS[cardOrder.prop];
  const {prepareExtractData, extractKey, keyToScalar = (k) => k} = primaryOrderInfo;

  const preparedData = prepareExtractData && prepareExtractData(bag);
  return cards.map((card) => {
    const keyAsValue = extractKey(card, preparedData);
    const keysAsArray = Array.isArray(keyAsValue) ? keyAsValue : [keyAsValue];
    return {card, scalarKeys: keysAsArray.map((k) => keyToScalar(k)), keys: keysAsArray};
  });
};

type OrderedCardGroupArg = {cards: any[]; cardOrder: LoadedCardOrder; bag: OrderBag};

export const getOrderedCardGroups = (opts: OrderedCardGroupArg) => {
  const {cardOrder, bag} = opts;
  const orderProp = cardOrder.isCompact ? null : cardOrder.prop;
  const {isLoaded, result} = checkIfPresent(() => {
    const cardsWithKeys = getCardGroups(opts);
    const cardAndKeys: CardWithKeys[] = [];
    cardsWithKeys.forEach(({card, scalarKeys, keys}, idx) => {
      const cardId = card.$meta.get("cardId", `none${idx}`);
      scalarKeys.forEach((key, i) =>
        cardAndKeys.push({
          card,
          scalarKey: key,
          key: keys[i],
          orderProp: cardOrder.isCompact ? null : cardOrder.prop,
          cardOrder,
          flipKey: `${orderProp}_${cardId}_${key}`,
        })
      );
    });
    return processGroups({cardAndKeys, bag, cardOrder});
  }, api);
  return {isLoaded, result};
};

export const SwimlaneDropArea = ({dragItem, orderInfo, orderKeyAsValue, label, isOver}: any) => {
  const {dropLabel, cannotDropLabel, cannotDrop: cannotDropFn} = orderInfo;
  const cards: any[] = dragItem
    ? dragItem.data.cardInfos.map(({cardId}: any) =>
        api.getModel({id: cardId, modelName: "card"} as any)
      )
    : [];
  const cannotDrop = cannotDropFn && cards.some((card) => cannotDropFn(orderKeyAsValue, card));
  const content =
    cards.length > 0 &&
    (cannotDrop ? cannotDropLabel(orderKeyAsValue, cards[0]) : dropLabel(orderKeyAsValue, label));
  return (
    <Col
      absolute
      style={{top: -8, bottom: -8, right: -8, left: -8}}
      zIndex={1}
      rounded={4}
      colorTheme={cannotDrop ? "alert25" : "active200"}
      borderWidth={1}
      borderColor="default"
      bg={isOver ? "foreground" : undefined}
      className={transitions.colors}
      align="center"
      pa="16px"
    >
      {isOver && content && (
        <StickyTopAwareStickyBox
          className={css({
            color: "primary",
            textType: "label14",
          })}
        >
          {content}
        </StickyTopAwareStickyBox>
      )}
    </Col>
  );
};
